import React, { useState, useRef } from 'react';
import styles from './OTPInput.module.css';

interface OTPInputProps {
  length?: number;
  onChangeOTP: (otp: string[]) => void;
  disabled?: boolean;
}

const OTPInput: React.FC<OTPInputProps> = ({ length = 6, onChangeOTP, disabled = false }) => {
  const [otp, setOTP] = useState<string[]>(new Array(length).fill(''));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (element: HTMLInputElement, index: number) => {
    // Only allow numeric input
    if (element.value && !/^[0-9]$/.test(element.value)) return;
    
    const newOTP = [...otp];
    newOTP[index] = element.value;
    setOTP(newOTP);
    
    // Auto-advance to next input on valid digit entry
    if (element.value && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
    
    // Pass OTP value to parent component
    if (onChangeOTP) onChangeOTP(newOTP);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    // Backspace: if current input is empty, move to previous input
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    
    // Extract only digits from pasted text
    const digits = pastedData.replace(/\D/g, '').slice(0, length);
    
    if (digits.length === 0) return;
    
    // Create new OTP array starting from current index
    const newOTP = [...otp];
    for (let i = 0; i < digits.length && (index + i) < length; i++) {
      newOTP[index + i] = digits[i];
    }
    
    setOTP(newOTP);
    
    // Focus the next empty input or the last input if all are filled
    const nextEmptyIndex = newOTP.findIndex((val, idx) => idx >= index && val === '');
    const targetIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : Math.min(index + digits.length, length - 1);
    inputsRef.current[targetIndex]?.focus();
    
    // Pass OTP value to parent component
    if (onChangeOTP) onChangeOTP(newOTP);
  };

  return (
    <div className={styles.otpInputContainer}>
      {otp.map((data, index) => (
        <input
          type="text"
          name="otp"
          maxLength={1}
          key={index}
          value={data}
          onChange={(e) => handleChange(e.target, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={(e) => handlePaste(e, index)}
          ref={(el) => { inputsRef.current[index] = el; }}
          className={styles.otpInput}
          disabled={disabled}
          inputMode="numeric"
        />
      ))}
    </div>
  );
};

export default OTPInput;
